import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import {
  Receipt,
  Plus,
  FileEdit,
  Send,
  Landmark,
  AlertTriangle,
  CircleDollarSign,
  CheckCircle2,
  Ban,
  Archive,
  ChevronDown,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  CalendarIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/StatusBadge';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FormDrawer } from '@/components/ui/form-drawer';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const INVOICE_STATUS_GROUPS = [
  {
    key: 'draft',
    label: 'Draft',
    icon: FileEdit,
    statuses: ['draft'],
    defaultExpanded: true,
  },
  {
    key: 'sent',
    label: 'Sent',
    icon: Send,
    statuses: ['sent'],
    defaultExpanded: true,
  },
  {
    key: 'deposit_requested',
    label: 'Deposit Requested',
    icon: Landmark,
    statuses: ['deposit_requested'],
    defaultExpanded: true,
  },
  {
    key: 'overdue',
    label: 'Overdue',
    icon: AlertTriangle,
    statuses: ['overdue'],
    defaultExpanded: true,
  },
  {
    key: 'partially_paid',
    label: 'Partially Paid',
    icon: CircleDollarSign,
    statuses: ['partially_paid'],
    defaultExpanded: true,
  },
  {
    key: 'paid',
    label: 'Paid',
    icon: CheckCircle2,
    statuses: ['paid'],
    defaultExpanded: false,
  },
  {
    key: 'voided',
    label: 'Voided',
    icon: Ban,
    statuses: ['voided'],
    defaultExpanded: false,
  },
  {
    key: 'closed',
    label: 'Closed',
    icon: Archive,
    statuses: ['closed'],
    defaultExpanded: false,
  },
];

const invoiceFormSchema = z.object({
  accountId: z.string().min(1, 'Please select an account'),
  dueDate: z.date(),
  paymentTerms: z.string().optional(),
  notes: z.string().optional(),
});

type InvoiceFormData = z.infer<typeof invoiceFormSchema>;

const PAYMENT_TERMS_OPTIONS = [
  { value: 'Net 15', label: 'Net 15' },
  { value: 'Net 30', label: 'Net 30' },
  { value: 'Net 45', label: 'Net 45' },
  { value: 'Net 60', label: 'Net 60' },
  { value: 'Due on Receipt', label: 'Due on Receipt' },
];

const formatCurrency = (value: string | number | null | undefined) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

interface Invoice {
  id: string;
  invoice_number: string;
  account_id: string;
  account_name?: string;
  status: string;
  invoice_date: string;
  due_date: string | null;
  total: string;
  balance: string;
  amount_paid: string;
  created_at: string;
  account?: { name: string };
}

function GroupRows({
  label,
  icon,
  count,
  totalValue,
  isExpanded,
  onToggle,
  invoices,
  onRowClick,
}: {
  label: string;
  icon: React.ReactNode;
  count: number;
  totalValue: number;
  isExpanded: boolean;
  onToggle: () => void;
  invoices: Invoice[];
  onRowClick: (row: Invoice) => void;
}) {
  return (
    <>
      <TableRow
        className="cursor-pointer bg-muted/50 border-b border-t border-border/50"
        onClick={onToggle}
      >
        <TableCell colSpan={7}>
          <div className="flex items-center gap-2 py-0.5">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            {icon}
            <span className="font-semibold text-sm">{label}</span>
            <Badge variant="secondary" className="no-default-active-elevate">{count}</Badge>
            <span className="text-xs text-muted-foreground ml-auto font-medium">
              {formatCurrency(totalValue)}
            </span>
          </div>
        </TableCell>
      </TableRow>
      {isExpanded &&
        invoices.map((row) => {
          const dueDate = row.due_date ? new Date(row.due_date) : null;
          const isOverdue = dueDate && dueDate < new Date() && !['paid', 'closed', 'voided'].includes(row.status);
          return (
            <TableRow
              key={row.id}
              onClick={() => onRowClick(row)}
              className="cursor-pointer"
            >
              <TableCell>
                <span className="text-muted-foreground">
                  {row.invoice_date ? new Date(row.invoice_date).toLocaleDateString() : '-'}
                </span>
              </TableCell>
              <TableCell>
                <span className="font-medium">{row.invoice_number}</span>
              </TableCell>
              <TableCell>
                <span>{row.account?.name || row.account_name || '-'}</span>
              </TableCell>
              <TableCell>
                <StatusBadge status={row.status} type="invoice" />
              </TableCell>
              <TableCell className="text-right">
                <span className="font-medium">{formatCurrency(row.total)}</span>
              </TableCell>
              <TableCell className="text-right">
                <span
                  className={Number(row.balance || 0) > 0 ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground'}
                >
                  {formatCurrency(row.balance)}
                </span>
              </TableCell>
              <TableCell>
                <span
                  className={isOverdue ? 'text-red-600 dark:text-red-500' : 'text-muted-foreground'}
                >
                  {dueDate ? dueDate.toLocaleDateString() : '-'}
                </span>
              </TableCell>
            </TableRow>
          );
        })}
    </>
  );
}

interface Account {
  id: string;
  name: string;
}

interface AccountsResponse {
  accounts: Account[];
  total: number;
}

export default function InvoicesPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { sessionId } = useAuth();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [sortKey, setSortKey] = useState<'dueDate' | 'invoiceDate' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const group of INVOICE_STATUS_GROUPS) {
      initial[group.key] = group.defaultExpanded;
    }
    return initial;
  });

  const { data, isLoading } = useQuery<{ invoices: Invoice[]; total: number }>({
    queryKey: ['invoices', sessionId, includeArchived],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '2000' });
      if (includeArchived) {
        params.append('includeArchived', 'true');
      }
      return api.get<{ invoices: Invoice[]; total: number }>(`/invoices?${params.toString()}`, sessionId);
    },
    enabled: !!sessionId,
  });

  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts', sessionId],
    queryFn: async () => {
      return api.get<AccountsResponse>('/accounts?limit=500', sessionId);
    },
    enabled: !!sessionId && newInvoiceOpen,
  });

  const accounts = accountsData?.accounts || [];

  const form = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: {
      accountId: '',
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      paymentTerms: 'Net 30',
      notes: '',
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: InvoiceFormData) => {
      return api.post<{ id: string; invoice_number: string }>('/invoices', {
        accountId: data.accountId,
        dueDate: data.dueDate.toISOString(),
        paymentTerms: data.paymentTerms,
        notes: data.notes,
        invoiceDate: new Date().toISOString(),
        status: 'draft',
      }, sessionId);
    },
    onSuccess: (invoice: { id: string; invoice_number: string }) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast({
        title: 'Invoice created',
        description: `Invoice ${invoice.invoice_number} has been created. Redirecting to add line items...`
      });
      setNewInvoiceOpen(false);
      form.reset();
      setLocation(`/invoices/${invoice.id}`);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to create invoice', variant: 'destructive' });
    },
  });

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSort = (key: 'dueDate' | 'invoiceDate') => {
    if (sortKey === key) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortKey(null);
        setSortDirection('asc');
      }
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortInvoices = (items: Invoice[]) => {
    if (!sortKey) return items;
    return [...items].sort((a, b) => {
      const aRaw = sortKey === 'dueDate' ? a.due_date : a.invoice_date;
      const bRaw = sortKey === 'dueDate' ? b.due_date : b.invoice_date;
      const aDate = aRaw ? new Date(aRaw) : null;
      const bDate = bRaw ? new Date(bRaw) : null;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      const diff = aDate.getTime() - bDate.getTime();
      return sortDirection === 'asc' ? diff : -diff;
    });
  };

  const invoices = data?.invoices || [];
  const grouped = INVOICE_STATUS_GROUPS.map((group) => ({
    ...group,
    invoices: sortInvoices(invoices.filter((inv) => group.statuses.includes(inv.status))),
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={includeArchived ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIncludeArchived((v) => !v)}
            >
              <Archive className="h-3.5 w-3.5 mr-1.5" />
              {includeArchived ? 'Hide archived' : 'Show archived'}
            </Button>
            <Button size="sm" onClick={() => setNewInvoiceOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Invoice
            </Button>
          </div>
        }
      />

      <div className="space-y-4">
        {invoices.length === 0 && !isLoading ? (
          <div className="rounded-md border">
            <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground py-12">
              <Receipt className="h-12 w-12 text-muted-foreground/50" />
              <p>No invoices found</p>
            </div>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="text-xs font-medium uppercase tracking-wide cursor-pointer select-none"
                    onClick={() => toggleSort('invoiceDate')}
                  >
                    <div className="flex items-center gap-1">
                      Invoice Date
                      {sortKey === 'invoiceDate' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide">Invoice #</TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide">Account</TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide">Status</TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide text-right">Total</TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide text-right">Balance</TableHead>
                  <TableHead
                    className="text-xs font-medium uppercase tracking-wide cursor-pointer select-none"
                    onClick={() => toggleSort('dueDate')}
                  >
                    <div className="flex items-center gap-1">
                      Due Date
                      {sortKey === 'dueDate' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />
                      )}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grouped.map((group) => {
                  if (group.invoices.length === 0) return null;
                  const isExpanded = expandedGroups[group.key];
                  const GroupIcon = group.icon;
                  const totalValue = group.invoices.reduce((sum, inv) => sum + Number(inv.total || 0), 0);

                  return (
                    <GroupRows
                      key={group.key}
                      label={group.label}
                      icon={<GroupIcon className="h-4 w-4" />}
                      count={group.invoices.length}
                      totalValue={totalValue}
                      isExpanded={isExpanded}
                      onToggle={() => toggleGroup(group.key)}
                      invoices={group.invoices}
                      onRowClick={(row) => setLocation(`/invoices/${row.id}`)}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {invoices.length > 0 && (
          <div className="text-sm text-muted-foreground">
            {invoices.length} invoice{invoices.length !== 1 ? 's' : ''} total
          </div>
        )}
      </div>

      {/* New Invoice Drawer */}
      <FormDrawer
        open={newInvoiceOpen}
        onOpenChange={setNewInvoiceOpen}
        title="New Invoice"
        description="Create a new invoice. You can add line items after creation."
        formId="create-invoice"
        isPending={createMutation.isPending}
        submitLabel="Create Invoice"
        size="lg"
        data-testid="drawer-new-invoice"
      >
        <Form {...form}>
          <form
            id="create-invoice"
            onSubmit={form.handleSubmit((data) => createMutation.mutate(data))}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="accountId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer Account *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || undefined}
                    disabled={accountsLoading}
                  >
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder={accountsLoading ? "Loading accounts..." : "Select an account"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accountsLoading ? (
                        <SelectItem value="loading" disabled>Loading accounts...</SelectItem>
                      ) : accounts.length === 0 ? (
                        <SelectItem value="no-accounts" disabled>No accounts available</SelectItem>
                      ) : (
                        accounts.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Due Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'justify-start text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(field.value, 'PPP') : 'Pick a date'}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={field.value} onSelect={field.onChange} />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="paymentTerms"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Terms</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger variant="flat">
                          <SelectValue placeholder="Select terms" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PAYMENT_TERMS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      variant="flat"
                      placeholder="Add any notes about this invoice..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
